import { get } from 'lodash';
import { Button, TextInput, Select, Typography, Grid } from '@ts/storybook';
import { typeRuleOperationsMap, operationDisplays } from '../Status/IndicatorType/utils';
import { WrapperEntitySelector, DateTimeInput, RadioButtonGroup } from '@components/Inputs';
import IntervalInput from '@components/Inputs/IntervalInput';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CEGRChangeStatus, CStatusIndicator, IForm } from '@ts/ui-models';

interface IEGRStatusChange extends IForm {
  values: CEGRChangeStatus;
  isReadOnly?: boolean;
}

const StatusChange = (props: IEGRStatusChange) => {
  const { onCallbackChange, values: EGR } = props;

  const plainIndicatorType = EGR.indicatorType;
  const isReadOnly = props.mode === 'view';

  const isNumberIndicatorType = (indicatorType: CStatusIndicator | undefined): boolean => {
    const type = indicatorType?.type;
    return type === 'integer' || type === 'float';
  };
  const isNumberIndicator = isNumberIndicatorType(plainIndicatorType);

  const onChangeEntitySelector = (entity, entityKey) => {
    const fkValue = entity ? entity.getID() : null;
    onCallbackChange(entityKey + 'Id')(fkValue);
    onCallbackChange(entityKey)(entity);
  };

  const renderConditionDefinition = () => {
    return (
      <Grid container spacing={2} direction={'column'}>
        <RadioButtonGroup
          title={'Type of condition'}
          value={EGR?.actionConfig?.conditionType || 'none'}
          onChange={onCallbackChange('actionConfig.conditionType')}
          options={[
            {
              value: 'static',
              label: ['Static', ': ', 'define a condition for the previous value and another for the new value'],
              disabled: isReadOnly,
            },
            {
              value: 'dynamic',
              label: ['Dynamic', ': ', 'define a condition for the difference between the previous and the new value'],
              disabled: isReadOnly || !isNumberIndicator,
            },
          ]}
        />
        <Condition {...props} isReadOnly={isReadOnly} />
      </Grid>
    );
  };

  return (
    <Grid container spacing={3} direction={'column'}>
      <WrapperEntitySelector
        label={'Status indicators'}
        searchComponent={generateSearchEntitySelector('indicatorType')}
        value={EGR.indicatorType ?? EGR.indicatorTypeId}
        onChangeEntity={entity => onChangeEntitySelector(entity, 'indicatorType')}
        isReadOnly={isReadOnly}
      />
      {renderConditionDefinition()}
    </Grid>
  );
};

const Condition = (props: IEGRStatusChange) => {
  const { values: EGR } = props;

  const StaticCondition = (props: IEGRStatusChange) => {
    const { isReadOnly, onCallbackChange, values: EGR } = props;

    const getOptionsForIndicatorType = (indicatorType: CStatusIndicator | undefined) => {
      const type = indicatorType?.type;
      const operationOptions = type ? get(typeRuleOperationsMap, type, []) : [];

      return operationOptions.map(op => ({
        label: operationDisplays[op],
        value: op,
      }));
    };
    const options = getOptionsForIndicatorType(EGR.indicatorType);

    //#region Renders
    const IndicatorTypeOperand = props => {
      if (!props.indicatorType) return null;
      const { indicatorType, value, onChange, selectedOperation } = props;

      const isIntervalOperation = selectedOperation == 'insideInterval' || selectedOperation == 'outsideInterval';

      switch (indicatorType.type) {
        case 'list':
          const possibleValues = indicatorType.possibleValues;
          const options = possibleValues.map(op => ({
            label: op,
            value: op,
          }));
          return <Select label={'Operand'} value={value || ''} onChange={onChange} isReadOnly={isReadOnly} options={options} />;
        case 'float':
        case 'integer':
        case 'distance':
        case 'speed':
          if (isIntervalOperation) return <IntervalInput value={value} onChange={onChange} isReadOnly={isReadOnly} />;
          else return <TextInput type="number" label="Operand" value={value} onChangeValue={onChange} isReadOnly={isReadOnly} />;
        case 'string':
          return <TextInput label="Operand" value={value} onChangeValue={onChange} isReadOnly={isReadOnly} />;
        case 'timestamp':
          return <DateTimeInput onChange={onChange} value={value} labelDate="Date" labelTime="Time" isReadOnly={isReadOnly} />;
        case 'date':
          return <DateTimeInput onlyDate labelDate={'Date'} value={value} onChange={onChange} isReadOnly={isReadOnly} />;
        case 'time':
          return (
            <DateTimeInput
              onlyTime
              labelTime={'Time'}
              value={value}
              onChange={onChange}
              style={{ minWidth: '90px' }}
              isReadOnly={isReadOnly}
            />
          );
        default:
          return <Typography text={'Indicator type not supported'} variant="body1" />;
      }
    };
    //#endregion
    return (
      <Grid container size={12} direction={'column'}>
        <Grid container size={12} direction={'column'}>
          <Typography className="font-bold" text="Indicator value changes from" />
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid size={6}>
              <Select
                label="Operation from"
                value={EGR.actionConfig?.from?.operation}
                onChange={onCallbackChange('actionConfig.from.operation')}
                isReadOnly={isReadOnly}
                options={options}
              />
            </Grid>
            <Grid size={4}>
              <IndicatorTypeOperand
                indicatorType={EGR.indicatorType}
                value={EGR.actionConfig?.from?.operand}
                selectedOperation={EGR.actionConfig?.from?.operation}
                onChange={onCallbackChange('actionConfig.from.operand')}
                isReadOnly={isReadOnly}
              />
            </Grid>
            {!isReadOnly && (
              <Grid size={'auto'}>
                <Button onClick={() => onCallbackChange('actionConfig.from')(null)} label="Clear" />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container size={12} direction={'column'}>
          <Typography className="font-bold" text="Indicator value changes to" />
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid size={6}>
              <Select
                label="Operation to"
                value={EGR.actionConfig?.to?.operation}
                onChange={onCallbackChange('actionConfig.to.operation')}
                isReadOnly={isReadOnly}
                options={options}
              />
            </Grid>
            <Grid size={4}>
              <IndicatorTypeOperand
                indicatorType={EGR.indicatorType}
                value={EGR.actionConfig?.to?.operand}
                selectedOperation={EGR.actionConfig?.to?.operation}
                onChange={onCallbackChange('actionConfig.to.operand')}
                isReadOnly={isReadOnly}
              />
            </Grid>
            {!isReadOnly && (
              <Grid size={'auto'}>
                <Button onClick={() => onCallbackChange('actionConfig.to')(null)} label="Clear" />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const DynamicCondition = (props: IEGRStatusChange) => {
    const { onCallbackChange, isReadOnly, values: EGR } = props;
    const options = [
      { value: 'incGreater', label: 'Increase greater or equal than' },
      { value: 'decGreater', label: 'Decrease greater or equal than' },
      { value: 'anyGreater', label: 'Any greater or equal than' },
      { value: 'incLesser', label: 'Increase lesser or equal than' },
      { value: 'decLesser', label: 'Decrease lesser or equal than' },
      { value: 'anyLesser', label: 'Any lesser or equal than' },
    ];

    const interpretationLabels = {
      absolute: 'The difference value is the absolute difference between the previous and the new value',
      percentage: 'The difference value is the percentage of difference between the previous and the new value',
    };
    const interpretationLabel =
      (EGR.actionConfig?.interpretationOfValue && interpretationLabels[EGR.actionConfig.interpretationOfValue]) || '';

    return (
      <Grid container spacing={2}>
        <Grid size={12}>
          <Select
            label={'Type of the change'}
            value={EGR.actionConfig?.typeOfChange}
            onChange={onCallbackChange('actionConfig.typeOfChange')}
            disabled={isReadOnly}
            options={options}
          />
        </Grid>
        <Grid size={4}>
          <TextInput
            type="number"
            label="Difference value"
            value={EGR.actionConfig?.conditionValue}
            onChangeValue={onCallbackChange('actionConfig.conditionValue')}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid size={4}>
          <Select
            label="Type of value"
            value={EGR.actionConfig?.interpretationOfValue || 'none'}
            onChange={onCallbackChange('actionConfig.interpretationOfValue')}
            disabled={isReadOnly}
            options={[
              {
                value: 'absolute',
                label: 'Absolute',
              },
              {
                value: 'percentage',
                label: 'Percentage',
              },
            ]}
          />
        </Grid>
        <Grid size={4}>
          <Typography text={interpretationLabel} />
        </Grid>
      </Grid>
    );
  };

  switch (EGR.actionConfig?.conditionType) {
    case 'static':
      return <StaticCondition {...props} />;
    case 'dynamic':
      return <DynamicCondition {...props} />;
    default:
      return null;
  }
};

export default StatusChange;
